import { IconButton, Popover } from '@mui/material';
import { useState, Fragment, useCallback, ReactElement, memo } from 'react';
import { KeyboardAlt } from '@mui/icons-material';
import { keyboard } from './constants';
import { Command, Key as KeyType } from './types';
import Key from './key';

interface AssistiveKeyboardProps {
  id: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  defaultUppercase?: boolean;
}

const AssistiveKeyboardComponent = ({
  onChange,
  id,
  defaultUppercase = false,
  disabled = false,
  onBlur = () => {
    return;
  },
}: AssistiveKeyboardProps): ReactElement => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [shift, setShift] = useState<boolean>(defaultUppercase);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    },
    [setAnchorEl]
  );

  const handleClose = useCallback(() => {
    return new Promise((resolve) => resolve(setAnchorEl(null))).then(() => onBlur());
  }, [setAnchorEl, onBlur]);

  const open = Boolean(anchorEl);

  const idKeyboard = open ? `${id}_keyboard` : undefined;

  const handleChange = useCallback(
    (key: KeyType) => {
      if (key.command === Command.Close) {
        handleClose();
        return;
      }

      if (key.command === Command.Uppercase) {
        setShift(!shift);
        return;
      }

      const keyPressed = shift ? key.value.toUpperCase() : key.value;

      onChange(keyPressed);
    },
    [onChange, handleClose, setShift, shift]
  );

  return (
    <Fragment>
      <IconButton onClick={handleClick} disabled={disabled}>
        <KeyboardAlt />
      </IconButton>
      <Popover
        id={idKeyboard}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        PaperProps={{
          style: {
            width: '51rem',
          },
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <div className="pmi-d-inline-block">
          {keyboard.map((key, index) => (
            <Key
              key={`${id}_${key.value}_${index + 1}`}
              {...key}
              uppercaseActive={shift || false}
              handleChange={handleChange}
            />
          ))}
        </div>
      </Popover>
    </Fragment>
  );
};

AssistiveKeyboardComponent.displayName = 'Assistive keyboard component';

const AssistiveKeyboard = memo(AssistiveKeyboardComponent);

export default AssistiveKeyboard as typeof AssistiveKeyboardComponent;
